import { DemoDocument } from './routes/demo';
import { AccessNotice, IfWrites } from './components/write-access';
import { CreditNotice } from './components/credit-notice';
import { StaleBuild } from './components/stale-build';
import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Accounts, AccountPage } from './routes/accounts';
import { useMayWrite, useOrg, useMe } from './api/app-context';
import { useAuth } from './auth/auth-context';
import { OrgSwitcher } from './components/org-switcher/org-switcher';
import { DevSwitch } from './components/ui/dev-switch';
import { ScrollReset } from './components/ui/scroll-reset';
import {
  EVENTS_TITLE,
  KNOWLEDGE_TITLE,
  NOT_FOUND,
  NOT_FOUND_LINE,
  ORGANISATIONS_TITLE,
} from './lib/copy';
import { orgHasProtectionAccounts } from './lib/vertical';
import { reportTypesFor } from '@ni/shared';
import { REPORTS_TITLE } from './lib/report-copy';
import { Reports } from './routes/reports/list';
import { NewReport } from './routes/reports/new';
import { ReportPage } from './routes/reports/report';
import { Home } from './routes/home';
import { KnowledgeBase } from './routes/knowledge';
import { Imports } from './routes/imports';
import { ImportSessionPage } from './routes/import/session';
import { ImportPlanTab } from './routes/import/plan';
import { ImportPeopleTab } from './routes/import/people';
import { ImportCommitTab } from './routes/import/commit';
import { ImportFileTab } from './routes/import/file';
import { NewOrganisation } from './routes/new-organisation';
import { NewPerson } from './routes/new-person';
import { Events } from './routes/events';
import { NewEvent } from './routes/new-event';
import { EventRecordPage } from './routes/event/record';
import { EventOverviewTab } from './routes/event/overview';
import { EventAnalysisTab } from './routes/event/analysis';
import { EventAttendeesTab } from './routes/event/attendees';
import { Organisations } from './routes/organisations';
import { OrganisationRecordPage } from './routes/organisation/record';
import { OrganisationOverviewTab } from './routes/organisation/overview';
import { OrganisationPeopleTab } from './routes/organisation/people';
import { OrganisationNetworkTab } from './routes/organisation/network';
import { OrganisationDataTab } from './routes/organisation/data';
import { People } from './routes/people';
import { Settings } from './routes/settings';
import { PersonRecord } from './routes/person/record';
import { Overview } from './routes/person/overview';
import { Research } from './routes/person/research';
import { SubjectDataTab } from './routes/person/data';
import { NetworkTab } from './routes/person/network';

/**
 * The shell: the demo's fixed nav rail and scrolling main column.
 *
 * The nav stays furniture-free (ui-design §2): the wordmark is the org name
 * and, for a member of several orgs, the switcher; the links are Home, People,
 * Organisations, the knowledge base, Imports and Settings, and the foot carries
 * who is signed in. People earned its line when the list outgrew Home (import
 * note §1.18) — it is a place, not a filter, and Home now points at it rather
 * than being it. Organisations earned its own the same way and beside it
 * (organisations-as-subjects note §1 R8): an organisation the office keeps a
 * record of is a thing it works on, not a filter of the people list.
 *
 * Text only, with Settings set apart and the reader and Sign out at the foot
 * (blend spec, "Rail"). Below 900px it folds to a top bar: the org name and a
 * Menu button that opens the links and the foot under it.
 */
function NavRail() {
  const me = useMe();
  const org = useOrg();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const rail = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // A link chosen from the folded menu is a departure: close it behind them.
  useEffect(() => setOpen(false), [pathname]);

  // Escape and a press outside close it, as the org switcher's menu does.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[aria-modal="true"]')) return;
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      if (!rail.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <nav className={open ? 'nav open' : 'nav'} ref={rail}>
      <OrgSwitcher />
      <button
        type="button"
        className="nav-menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Menu
      </button>
      <ul>
        <li>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : undefined)}>
            Home
          </NavLink>
        </li>
        <li>
          {/* `end` is wrong here: `/people/:id` and `/people/new` are this
              section's pages, and the rail should say so on all of them. */}
          <NavLink to="/people" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            People
          </NavLink>
        </li>
        <li>
          {/* **Beside People, and after it** (organisations-as-subjects note §1
              R8). The two lists are the office's own two rosters and read as a
              pair; People is first because it is what the office works on most.
              `end` is wrong here for the same reason it is wrong on People:
              `/organisations/new` and an organisation's record are this
              section's pages, and the rail should say so on all of them. */}
          <NavLink
            to="/organizations"
            className={({ isActive }) => (isActive ? 'active' : undefined)}
          >
            {ORGANISATIONS_TITLE}
          </NavLink>
        </li>
        {/* **Events** (event graph design §3a): every vertical, after the two rosters. */}
        <li>
          <NavLink to="/events" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            {EVENTS_TITLE}
          </NavLink>
        </li>
        {/* **The security vertical's one page of its own** (security fold,
            2026-09-22). Everything else a protection firm uses is the same
            People, Organizations, knowledge base and Imports every other
            vertical uses, narrowed by an account; this is where the accounts
            themselves are. Beside the two rosters, because it is the third
            thing the office keeps rather than a setting. */}
        {orgHasProtectionAccounts(org) && (
          <li>
            <NavLink to="/accounts" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Protection accounts
            </NavLink>
          </li>
        )}
        {/* **Reports** (reports design §2): after the rosters and the accounts,
            because they are what the office keeps and this is what it
            produces; hidden where the vertical offers no report type. */}
        {reportTypesFor(org.vertical).length > 0 && (
          <li>
            <NavLink to="/reports" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              {REPORTS_TITLE}
            </NavLink>
          </li>
        )}
        <li>
          {/* Between People and Imports on purpose (knowledge-base note §2):
              People is who the office works on, this is everything the system
              holds, and Imports is where most of it came from. One page, no
              section under it — `end` would be right here and is simply not
              needed, since nothing lives below `/knowledge`. */}
          <NavLink to="/knowledge" className={({ isActive }) => (isActive ? 'active' : undefined)}>
            {KNOWLEDGE_TITLE}
          </NavLink>
        </li>
        <li>
          {/* `end` is wrong here for the reason it is wrong on People: a
              session page is this section's page, and the rail should say so
              while the office is on one. */}
          <IfWrites>
            <NavLink to="/imports" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Imports
            </NavLink>
          </IfWrites>
        </li>
        <li className="nav-apart">
          <IfWrites>
            <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              Settings
            </NavLink>
          </IfWrites>
        </li>
      </ul>

      <div className="nav-foot">
        <span className="who">{me.user.name ?? me.user.email}</span>

        {signOut ? (
          <button type="button" className="quiet-action" onClick={signOut}>
            Sign out
          </button>
        ) : null}

        <DevSwitch />
      </div>
    </nav>
  );
}

/**
 * **The address the office already shared.** The roster and the record moved from
 * `/organisations` to `/organizations` on 2026-09-10, when the product settled on
 * American English throughout. A link is a thing people paste into mail and keep
 * in a bookmark, so the old spelling keeps answering — permanently, and with the
 * rest of the address carried over untouched, so a tab and a query string that
 * were part of the link still are.
 *
 * `replace` because the old address is not a place: a reader who lands here and
 * presses Back wants the page they came from, not a bounce back through the
 * spelling they no longer have.
 */
export function movedOrganisationsAddress(at: {
  pathname: string;
  search: string;
  hash: string;
}): string {
  return `${at.pathname.replace(/^\/organisations/, '/organizations')}${at.search}${at.hash}`;
}

function OldOrganisationsAddress() {
  return <Navigate to={movedOrganisationsAddress(useLocation())} replace />;
}

function NotFound() {
  return (
    <div className="column">
      <div className="head">
        <div className="titles">
          <div className="title">{NOT_FOUND}</div>
        </div>
      </div>
      <p className="lede">{NOT_FOUND_LINE}</p>
    </div>
  );
}

/**
 * **One tree, every vertical** (security fold, 2026-09-22).
 *
 * The security vertical used to be served a parallel router — its own Home,
 * its own People and Organizations, its own record page — because its records
 * lived in a parallel substrate the standard pages could not read. Per-account
 * entities made every one of those pages the standard page narrowed by a
 * filter, so there is one tree again, and a security org's only addition is the
 * Protection accounts pair below.
 */
export function App() {
  const org = useOrg();
  /* **Not `is_demo` any more.** These five addresses are the owner's, and a demo
     organization is only one of the two reasons somebody may not be one —
     `useMayWrite` folds both, in the same order the server does. The answer is
     still Not found rather than a refusal: a page whose every control is absent
     is not a page. */
  const ownerOnly = !useMayWrite('all');
  const security = orgHasProtectionAccounts(org);
  return (
    <>
      <ScrollReset />
      <NavRail />
      <StaleBuild />
      <AccessNotice />
      <CreditNotice />
      <main className="main">
        <Routes>
          <Route path="/demo/evidence/:evidenceId" element={<DemoDocument />} />
          {/* Mounted for every vertical and refused inside, the way the moved
              organisations address is answered rather than 404'd: a pasted link
              should say where it does not apply rather than read as a page that
              does not exist. */}
          <Route path="/accounts" element={security ? <Accounts /> : <Navigate to="/" replace />} />
          <Route
            path="/accounts/:accountId"
            element={security ? <AccountPage /> : <Navigate to="/" replace />}
          />
          <Route path="/" element={<Home />} />
          {/* The Auth0 redirect target. The provider exchanges the code and
              puts the address bar back before the router mounts, so this only
              catches a reload of `/callback` with a live session — which would
              otherwise read as Not found. */}
          <Route path="/callback" element={<Navigate to="/" replace />} />
          <Route path="/people" element={<People />} />
          <Route path="/people/new" element={ownerOnly ? <NotFound /> : <NewPerson />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/new" element={ownerOnly ? <NotFound /> : <NewEvent />} />
          <Route path="/events/:eventEntityId" element={<EventRecordPage />}>
            <Route index element={<EventOverviewTab />} />
            <Route path="attendees" element={<EventAttendeesTab />} />
            <Route path="analysis" element={<EventAnalysisTab />} />
          </Route>
          <Route path="/organizations" element={<Organisations />} />
          <Route
            path="/organizations/new"
            element={ownerOnly ? <NotFound /> : <NewOrganisation />}
          />
          {/* The record page. It takes the address's own id rather than a
              subject id, an organisation having only the one, and the page folds
              its case at the params boundary. Three tabs since the owner's
              rulings of 2026-09-09 and 2026-09-10, route-addressed as a person's
              are: Overview is the index, so a bare `/organizations/:id` opens on
              the reading the office wants nine times in ten. */}
          <Route path="/organizations/:orgEntityId" element={<OrganisationRecordPage />}>
            <Route index element={<OrganisationOverviewTab />} />
            {/* The leadership and the investors, cut out of the Overview when it
                grew past one screen (owner ruling, 2026-09-10). */}
            <Route path="people" element={<OrganisationPeopleTab />} />
            <Route path="network" element={<OrganisationNetworkTab />} />
            {/* Data is a route and not a tab, exactly as a person's is (owner
                ruling, 2026-09-10): it is reached from the button in the
                record's foot, and the shell above it still draws the breadcrumb
                and the tabs, so the way back is the way back from any
                reading of the record. */}
            <Route path="data" element={<OrganisationDataTab />} />
          </Route>
          {/* The spelling the office shared links under before 2026-09-10. It
              answers with the new address and nothing else — see
              `OldOrganisationsAddress`. */}
          <Route path="/organisations" element={<OldOrganisationsAddress />} />
          <Route path="/organisations/*" element={<OldOrganisationsAddress />} />
          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/new" element={ownerOnly ? <NotFound /> : <NewReport />} />
          <Route path="/reports/:reportId" element={<ReportPage />} />
          <Route path="/settings" element={ownerOnly ? <NotFound /> : <Settings />} />
          <Route path="/imports" element={ownerOnly ? <NotFound /> : <Imports />} />
          <Route
            path="/imports/:importId"
            element={ownerOnly ? <NotFound /> : <ImportSessionPage />}
          >
            <Route index element={<ImportPlanTab />} />
            <Route path="people" element={<ImportPeopleTab />} />
            <Route path="import" element={<ImportCommitTab />} />
            <Route path="file" element={<ImportFileTab />} />
          </Route>
          <Route path="/people/:subjectId" element={<PersonRecord />}>
            <Route index element={<Overview />} />
            <Route path="research" element={<Research />} />
            <Route path="network" element={<NetworkTab />} />
            <Route path="data" element={<SubjectDataTab />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </>
  );
}
